import React from "react";
import {
  Eye, EyeOff, ChevronUp, ChevronDown, Copy, Trash2, Layers as LayersIcon,
  SlidersHorizontal, Boxes, Link2, Unlink, Minus, Plus,
} from "lucide-react";
import { useCad } from "../context/CadContext";
import { levelColor } from "../constants/defaults";
import { resolveConnector } from "../lib/geometry";
import { AssemblyInspector } from "./AssemblyInspector";
import { Input } from "./ui/input";

const paramFields = {
  rectangle: [["w", "Width"], ["h", "Height"]],
  circle: [["r", "Radius"]],
  triangle: [["r", "Radius"]],
  star: [["points", "Points"], ["outerR", "Outer R"], ["innerR", "Inner R"]],
  polygon: [["sides", "Sides"], ["r", "Radius"]],
};

function NumberField({ label, value, onChange, step = 1, min, testid, suffix }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="relative mt-0.5">
        <input
          data-testid={testid}
          type="number"
          value={value}
          step={step}
          min={min}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-full h-8 rounded-md bg-slate-950/60 border border-slate-700 px-2 text-xs font-mono text-slate-100 focus:border-emerald-500 focus:outline-none"
        />
        {suffix && <span className="absolute right-2 top-1.5 text-[10px] text-slate-600 font-mono">{suffix}</span>}
      </div>
    </label>
  );
}

export function RightPanel() {
  const {
    layers, selected, selectedId, setSelectedId, updatePlate, updateParams,
    deletePlate, duplicatePlate, moveLayer, moveShapeLevel, toggleUnite, tool, settings,
    selectedConnector, setSelectedConnector, updateConnector, deleteConnector,
  } = useCad();

  const ordered = [...layers].reverse(); // top first

  return (
    <aside className="w-72 lg:w-80 shrink-0 bg-[#0B1120] border-l border-slate-800 flex flex-col overflow-hidden">
      {/* Layers */}
      <div className="border-b border-slate-800 flex flex-col max-h-[42%]">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <LayersIcon size={13} className="text-emerald-400" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Layers</span>
          <span className="ml-auto text-[10px] font-mono text-slate-600">{layers.length} · bottom→top</span>
        </div>
        <div className="overflow-y-auto cad-scroll px-2 pb-2 space-y-1">
          {ordered.length === 0 && (
            <div className="text-[11px] text-slate-600 px-2 py-3">No layers. Add a plate from the left.</div>
          )}
          {ordered.map((l) => {
            const sel = l.id === selectedId;
            const stackIdx = layers.findIndex((x) => x.id === l.id);
            return (
              <div
                key={l.id}
                data-testid={`layer-row-${l.id}`}
                onClick={() => { setSelectedId(l.id); setSelectedConnector(null); }}
                className={`group flex items-center gap-2 px-2 py-1.5 rounded-md border cursor-pointer transition-colors duration-150 ${
                  sel ? "bg-emerald-500/10 border-emerald-500/40" : "border-transparent hover:bg-slate-800/60"
                }`}
              >
                <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: levelColor(l.level) }} />
                <span className="text-xs text-slate-200 truncate flex-1">{l.name}</span>
                <span className="text-[9px] font-mono text-slate-500 px-1 rounded bg-slate-800">L{l.level}</span>
                <span className="text-[10px] font-mono text-slate-600">{l.connectors.length}⚲</span>
                <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <MiniBtn testid={`layer-vis-${l.id}`} onClick={(e) => { e.stopPropagation(); updatePlate(l.id, { visible: !(l.visible !== false) }); }}>
                    {l.visible !== false ? <Eye size={13} /> : <EyeOff size={13} className="text-slate-600" />}
                  </MiniBtn>
                  <MiniBtn testid={`layer-up-${l.id}`} disabled={stackIdx === layers.length - 1} onClick={(e) => { e.stopPropagation(); moveLayer(l.id, 1); }}>
                    <ChevronUp size={13} />
                  </MiniBtn>
                  <MiniBtn testid={`layer-down-${l.id}`} disabled={stackIdx === 0} onClick={(e) => { e.stopPropagation(); moveLayer(l.id, -1); }}>
                    <ChevronDown size={13} />
                  </MiniBtn>
                  <MiniBtn testid={`layer-dup-${l.id}`} onClick={(e) => { e.stopPropagation(); duplicatePlate(l.id); }}>
                    <Copy size={12} />
                  </MiniBtn>
                  <MiniBtn testid={`layer-del-${l.id}`} onClick={(e) => { e.stopPropagation(); deletePlate(l.id); }}>
                    <Trash2 size={12} className="text-rose-400" />
                  </MiniBtn>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AssemblyInspector />

      {/* Properties */}
      <div className="flex-1 overflow-y-auto cad-scroll">
        {!selected ? (
          <div className="p-4 text-[11px] text-slate-600 leading-relaxed">
            Select a layer to edit its geometry, position and snap-fit connectors.
          </div>
        ) : (
          <div className="p-3 space-y-4">
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={13} className="text-emerald-400" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Plate Properties</span>
            </div>

            <label className="block">
              <span className="text-[10px] text-slate-500 uppercase tracking-wide">Name</span>
              <Input
                data-testid="plate-name-input"
                value={selected.name}
                onChange={(e) => updatePlate(selected.id, { name: e.target.value })}
                className="mt-0.5 h-8 bg-slate-950/60 border-slate-700 text-xs text-slate-100"
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              {(paramFields[selected.type] || []).map(([key, lbl]) => (
                <NumberField
                  key={key}
                  testid={`param-${key}-input`}
                  label={lbl}
                  value={selected.params[key]}
                  step={key === "sides" || key === "points" ? 1 : 0.5}
                  min={key === "sides" ? 3 : 0}
                  suffix={key === "sides" || key === "points" ? "" : "mm"}
                  onChange={(v) => updateParams(selected.id, { [key]: v })}
                />
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <NumberField testid="pos-x-input" label="Position X" value={selected.x} step={0.5} suffix="mm" onChange={(v) => updatePlate(selected.id, { x: v })} />
              <NumberField testid="pos-y-input" label="Position Y" value={selected.y} step={0.5} suffix="mm" onChange={(v) => updatePlate(selected.id, { y: v })} />
              <NumberField testid="rotation-input" label="Rotation" value={selected.rotation} step={5} suffix="°" onChange={(v) => updatePlate(selected.id, { rotation: v })} />
              <NumberField testid="thickness-input" label="Thickness" value={selected.thickness} step={0.5} min={0.1} suffix="mm" onChange={(v) => updatePlate(selected.id, { thickness: v || 0.1 })} />
            </div>

            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wide">Edge finish</span>
              <div className="flex gap-2 mt-1">
                <div className="flex gap-1 flex-1">
                  {["chamfer", "fillet"].map((s) => (
                    <button
                      key={s}
                      data-testid={`plate-edge-${s}`}
                      onClick={() => updatePlate(selected.id, { edgeType: s })}
                      className={`flex-1 h-8 rounded-md text-[11px] capitalize transition-colors duration-150 ${
                        (selected.edgeType || "chamfer") === s ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-950/50 text-slate-400 border border-slate-700 hover:bg-slate-800"
                      }`}
                    >{s}</button>
                  ))}
                </div>
                <div className="w-24">
                  <NumberField testid="plate-edgeChamfer-input" label="Edge" value={selected.edgeChamfer ?? 0} step={0.1} min={0} suffix="mm" onChange={(v) => updatePlate(selected.id, { edgeChamfer: v })} />
                </div>
              </div>
            </div>

            <div>
              <span className="text-[10px] text-slate-500 uppercase tracking-wide">Layer level (colour-coded)</span>
              <div className="flex items-center gap-2 mt-1">
                <button data-testid="level-down-btn" onClick={() => moveShapeLevel(selected.id, -1)} className="h-8 w-8 grid place-items-center rounded-md bg-slate-950/50 border border-slate-700 text-slate-300 hover:bg-slate-800"><Minus size={14} /></button>
                <span className="flex-1 text-center text-xs font-mono flex items-center justify-center gap-1.5" style={{ color: levelColor(selected.level) }}>
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: levelColor(selected.level) }} /> Level {selected.level}
                </span>
                <button data-testid="level-up-btn" onClick={() => moveShapeLevel(selected.id, 1)} className="h-8 w-8 grid place-items-center rounded-md bg-slate-950/50 border border-slate-700 text-slate-300 hover:bg-slate-800"><Plus size={14} /></button>
              </div>
            </div>

            {/* Connectors */}
            <div className="pt-2 border-t border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <Boxes size={13} className="text-sky-400" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400">Connectors</span>
                <button
                  data-testid="unite-toggle-btn"
                  onClick={() => toggleUnite(selected.id)}
                  title={selected.united ? "Connectors move with the shape" : "Connectors stay put when moving the shape"}
                  className={`ml-auto h-6 px-2 rounded-md text-[10px] font-mono flex items-center gap-1 transition-colors duration-150 ${
                    selected.united ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-white"
                  }`}
                >
                  {selected.united ? <Link2 size={12} /> : <Unlink size={12} />} {selected.united ? "United" : "Free"}
                </button>
                <span className="text-[10px] font-mono text-slate-600">{selected.connectors.length}</span>
              </div>
              {selected.connectors.length === 0 ? (
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  {tool === "connector"
                    ? "Click on the plate in the 2D canvas to place a snap-fit point."
                    : "Switch to the Connector tool and click the plate to add snap-fit points."}
                </p>
              ) : (
                <div className="space-y-2">
                  {selected.connectors.map((cn, i) => {
                    const active = cn.id === selectedConnector;
                    const r = resolveConnector(cn, settings);
                    return (
                      <div key={cn.id} data-testid={`connector-row-${cn.id}`} className="rounded-lg border border-slate-800 bg-slate-950/40 overflow-hidden">
                        <div
                          onClick={() => setSelectedConnector(active ? null : cn.id)}
                          className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer ${active ? "bg-sky-500/10" : "hover:bg-slate-800/60"}`}
                        >
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: levelColor(selected.level) }} />
                          <span className="text-xs text-slate-200">Snap #{i + 1}</span>
                          <span className="text-[10px] font-mono text-slate-500">({cn.x}, {cn.y})</span>
                          {cn.override && <span className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-400 font-mono">OVR</span>}
                          <button
                            data-testid={`connector-del-${cn.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteConnector(selected.id, cn.id); }}
                            className="ml-auto text-slate-500 hover:text-rose-400"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                        {active && (
                          <div className="p-2.5 border-t border-slate-800 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-slate-500 uppercase tracking-wide">Override defaults</span>
                              <button
                                data-testid={`connector-override-toggle-${cn.id}`}
                                onClick={() => updateConnector(selected.id, cn.id, { override: cn.override ? null : { ...r } })}
                                className={`text-[10px] font-mono px-2 py-0.5 rounded ${cn.override ? "bg-amber-500/20 text-amber-400" : "bg-slate-800 text-slate-400"}`}
                              >
                                {cn.override ? "ON" : "OFF"}
                              </button>
                            </div>
                            {cn.override ? (
                              <>
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="col-span-2 block">
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wide">Shape</span>
                                    <div className="flex gap-1 mt-0.5">
                                      {["square", "diamond"].map((s) => (
                                        <button
                                          key={s}
                                          data-testid={`connector-shape-${s}-${cn.id}`}
                                          onClick={() => updateConnector(selected.id, cn.id, { override: { ...cn.override, shape: s } })}
                                          className={`flex-1 h-7 rounded text-[11px] capitalize ${cn.override.shape === s ? "bg-sky-500/20 text-sky-300 border border-sky-500/40" : "bg-slate-900 text-slate-400 border border-slate-700"}`}
                                        >{s}</button>
                                      ))}
                                    </div>
                                  </label>
                                  <OvrField cn={cn} k="femaleSize" label="Female" step={0.1} update={updateConnector} pid={selected.id} r={r} />
                                  <OvrField cn={cn} k="tolerance" label="Tol/side" step={0.01} update={updateConnector} pid={selected.id} r={r} />
                                  <OvrField cn={cn} k="maleHeight" label="Male H" step={0.1} update={updateConnector} pid={selected.id} r={r} />
                                  <OvrField cn={cn} k="femaleExtra" label="Female +" step={0.05} update={updateConnector} pid={selected.id} r={r} />
                                  <OvrField cn={cn} k="chamfer" label="Chamfer" step={0.01} update={updateConnector} pid={selected.id} r={r} />
                                </div>
                              </>
                            ) : (
                              <div className="text-[10px] font-mono text-slate-500 grid grid-cols-2 gap-x-3 gap-y-0.5">
                                <span>male {r.maleSize.toFixed(2)}mm</span>
                                <span>female {r.femaleSize}mm</span>
                                <span>peg h {r.maleHeight}mm</span>
                                <span>hole d {r.femaleDepth.toFixed(2)}mm</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function OvrField({ cn, k, label, step, update, pid, r }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</span>
      <input
        data-testid={`connector-${k}-${cn.id}`}
        type="number"
        step={step}
        value={cn.override[k] ?? r[k]}
        onChange={(e) => update(pid, cn.id, { override: { ...cn.override, [k]: Number(e.target.value) } })}
        className="mt-0.5 w-full h-7 rounded-md bg-slate-950/60 border border-slate-700 px-2 text-[11px] font-mono text-slate-100 focus:border-sky-500 focus:outline-none"
      />
    </label>
  );
}

const MiniBtn = ({ children, onClick, disabled, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    disabled={disabled}
    className="h-6 w-6 grid place-items-center rounded text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-25 disabled:hover:bg-transparent transition-colors duration-150"
  >
    {children}
  </button>
);
